import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import JsonTableRenderer from '../Common/JsonTableRenderer';
import { extractWidgetStructuredData, looksLikeHtmlPayload, normalizeWidgetResponsePayload } from '../../Utils/widgetResponseUtils';

const parsed = value => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
};

export const fieldLabel = key => String(key).replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');

export default function ProjectChatData({ value, fields = [], widget = false, depth = 0 }) {
  const { t, i18n } = useTranslation();
  let data = parsed(value);
  if (widget && data && typeof data === 'object' && ['answer', 'data_json', 'ui_template', 'schema_json'].some(key => key in data)) {
    const payload = normalizeWidgetResponsePayload(data);
    data = extractWidgetStructuredData(payload) ?? payload.answer ?? null;
    if (typeof data === 'string' && looksLikeHtmlPayload(data)) data = null;
  }
  const label = key => fields.find(field => field.key === key)?.display_label
    || (key === 'value' ? t('project_chat.readable.value') : fieldLabel(key));
  const cell = (item, key) => {
    if (item == null) return t('project_chat.readable.unknown');
    if (typeof item === 'boolean') return t(item ? 'project_chat.readable.yes' : 'project_chat.readable.no');
    if (typeof item === 'number') {
      const field = fields.find(field => field.key === key);
      try {
        return new Intl.NumberFormat(i18n.language, field?.display_format === 'currency' && /^[A-Z]{3}$/.test(field.currency)
          ? { style: 'currency', currency: field.currency } : {}).format(item);
      } catch { return String(item); }
    }
    if (typeof item === 'object') return depth < 2
      ? <ProjectChatData value={item} depth={depth + 1} /> : t('project_chat.readable.more_detail');
    return String(item).length > 2000 ? `${String(item).slice(0, 2000)}…` : String(item);
  };
  if (data == null) return <Typography variant="body2">{t('project_chat.readable.unavailable')}</Typography>;
  if (typeof data !== 'object') return <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{cell(data)}</Typography>;
  const limit = depth ? 5 : 30;
  const count = Array.isArray(data) ? data.length : Object.keys(data).length;
  const bounded = Array.isArray(data) ? data.slice(0, limit) : Object.fromEntries(Object.entries(data).slice(0, limit));
  if (!count) return <Typography variant="body2">{t('project_chat.readable.empty')}</Typography>;
  return <Box sx={{ minWidth: 0, overflowX: 'auto' }}>
    <JsonTableRenderer data={bounded} maxItems={limit} showIndex={false} maxHeight={320} compact={false}
      labels={{ key: t('project_chat.readable.item'), value: t('project_chat.readable.value'),
        unknown: t('project_chat.readable.unknown'), fieldLabel: label, formatValue: cell }} />
    {count > limit && <Typography variant="caption">{t('project_chat.readable.limited', { count: limit, total: count })}</Typography>}
  </Box>;
}
